import type { NavigateFunction } from 'react-router-dom'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'

/** Project config snapshot (minimal shape) from config store. */
export function navigateAfterOutputsReady(
  navigate: NavigateFunction,
  projectId: string,
  config: {
    pages?: {
      result?: {
        settings?: {
          postSessionAi?: {
            enabled?: boolean
            sourceAiEffectId?: string
            effectPrompt?: string
          } | null
          postSessionAiPaymentEnabled?: boolean
          postSessionAiPackages?: Array<{ isActive?: boolean }> | null
        } | null
      } | null
    } | null
  } | null
): void {
  // Feature-flag gate (super-admin kill switch). Default true so a missing/unfetched
  // flag map (offline first boot) keeps the live feature available — the flag can only
  // turn the AI step OFF, never silently strand a booth that expected it.
  const aiEffectsFlagOn = useFeatureFlagsStore.getState().getFlag('ai_effects_app', true)
  if (!aiEffectsFlagOn) {
    navigate(`/${projectId}/result`)
    return
  }

  const settings = config?.pages?.result?.settings
  const ai = settings?.postSessionAi
  const enabled = ai?.enabled === true
  if (!enabled) {
    navigate(`/${projectId}/result`)
    return
  }

  // "Enabled" alone is not enough: with no concrete effect configured (no library effect selected
  // and no custom prompt) the server has nothing to apply, so the AI step would just show an empty
  // preview and an Apply that no-ops. Treat that as off and go straight to the result instead of
  // stranding the guest. Mirrors the dashboard warning in PostSessionAiSettings.
  const effectConfigured =
    (typeof ai?.sourceAiEffectId === 'string' && ai.sourceAiEffectId.trim().length > 0) ||
    (typeof ai?.effectPrompt === 'string' && ai.effectPrompt.trim().length > 0)
  if (!effectConfigured) {
    navigate(`/${projectId}/result`)
    return
  }

  // Hybrid charging model (mirrors the server in /api/ai/post-session):
  //  - PAID config (AI-effect payment on + at least one active package): the guest pays
  //    per package, so the effect is always offered regardless of owner credits.
  //  - FREE config: the effect is funded by the owner's AI credits. With no usable credits
  //    the booth would only error on "Apply", so skip the dead-end step and go to Result.
  const guestPays =
    settings?.postSessionAiPaymentEnabled === true &&
    Array.isArray(settings?.postSessionAiPackages) &&
    settings.postSessionAiPackages.some((p) => p && p.isActive !== false)

  if (!guestPays) {
    // Same "has funding" semantic the booth uses elsewhere (checkCredits):
    // a numeric balance must be > 0; "Unlimited" (valid subscription) always passes.
    const { credits } = useCreditsStore.getState()
    const hasFunding = typeof credits === 'number' ? credits > 0 : true
    if (!hasFunding) {
      navigate(`/${projectId}/result`)
      return
    }
  }

  navigate(`/${projectId}/post-session-ai`)
}
